import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Typography, Descriptions, Tag, Button, Spin, App, Card, Input, InputNumber, Space, Divider, Table, Tooltip, Flex, theme } from 'antd';
import { ArrowLeftOutlined, HeartOutlined, SaveOutlined } from '@ant-design/icons';
import apiClient from '../api/client';
import { useAuth } from '../store/AuthContext';
import { STATUS_COLORS, timeAgo } from '../components/health/statusVisuals';

const { Title, Text, Paragraph } = Typography;

interface Server {
  id: string;
  key: string;
  name: string;
  mainIP: string;
  networks: string[];
  checkMode: string;
  type?: 'dedicated' | 'mobile-gateway';
  country: string;
  tags: string[];
  enabled: boolean;
  sourceKey?: string;
  sourceId?: string;
  sourceUrl?: string;
  meta?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface AgentConfig {
  serverKey: string;
  listenerUrl: string;
  interval: number;
  networks: string[];
}

interface HeartbeatInfo {
  lastHeartbeat: string | null;
  agentVersion: string;
  sourceIP: string;
}

interface StepResult {
  name: string;
  success: boolean;
  latency: number;
  error?: string;
}

interface IPHealth {
  ip: string;
  state: string;
  latency: number;
  steps: StepResult[];
  checkedAt: string;
}

interface ProxyHealth {
  protocol: string;
  host: string;
  port: number;
  label?: string;
  state: string;
  latency: number;
  steps: StepResult[];
  checkedAt: string;
}

interface HealthDetail {
  status: string;
  ips: IPHealth[];
  proxies: ProxyHealth[];
  heartbeat?: HeartbeatInfo;
}

// One row of the health summary — networks and proxies flattened into a single
// list, because the question being answered ("what is broken on this server?")
// does not care which of the two a component is.
interface HealthRow {
  key: string;
  kind: 'network' | 'proxy';
  target: string;
  protocol?: string;
  label?: string;
  state: string;
  latency: number;
  failedStep?: string;
  error?: string;
  checkedAt: string;
}

const HEALTHY_STATES = ['HEALTHY', 'UNKNOWN'];

const STEP_LABELS: Record<string, string> = {
  icmp_ping: 'ICMP Ping',
  globalping: 'Global Visibility',
  geoip: 'GeoIP',
  proxy_connect: 'Proxy TCP Connect',
  proxy_request: 'Proxy HTTP Request',
  proxy_latency: 'Tunnel Latency',
  proxy_egress_geo: 'Egress Geo',
};

// firstFailure returns the step that broke, with its error — the two things an
// operator needs before deciding whether to touch the server at all. The keys
// must match HealthRow's: the result is spread into the row, and a spread is
// exempt from excess-property checking, so a typo here fails silently.
function firstFailure(steps: StepResult[]): Pick<HealthRow, 'failedStep' | 'error'> {
  const failed = (steps || []).find((s) => !s.success);
  if (!failed) return {};
  return { failedStep: STEP_LABELS[failed.name] || failed.name, error: failed.error };
}

function buildHealthRows(detail: HealthDetail | null): HealthRow[] {
  if (!detail) return [];
  const rows: HealthRow[] = [];
  (detail.ips || []).forEach((ip) => {
    rows.push({
      key: `ip:${ip.ip}`,
      kind: 'network',
      target: ip.ip,
      state: ip.state,
      latency: ip.latency,
      checkedAt: ip.checkedAt,
      ...firstFailure(ip.steps),
    });
  });
  (detail.proxies || []).forEach((p) => {
    rows.push({
      key: `proxy:${p.host}:${p.port}`,
      kind: 'proxy',
      target: `${p.host}:${p.port}`,
      protocol: p.protocol,
      label: p.label,
      state: p.state,
      latency: p.latency,
      checkedAt: p.checkedAt,
      ...firstFailure(p.steps),
    });
  });
  // Worst first: the reason the page was opened should be the first thing on it.
  const rank: Record<string, number> = {
    DOWN: 7, ERROR: 6, ZOMBIE: 5, DEGRADED: 4, STALE: 3, SUSPECT: 2, UNKNOWN: 1, HEALTHY: 0,
  };
  return rows.sort((a, b) => (rank[b.state] ?? 0) - (rank[a.state] ?? 0));
}


const ServerDetailPage: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { user } = useAuth();
  const { token } = theme.useToken();
  const [server, setServer] = useState<Server | null>(null);
  const [loading, setLoading] = useState(true);
  const [agentConfig, setAgentConfig] = useState<AgentConfig | null>(null);
  const [heartbeat, setHeartbeat] = useState<HeartbeatInfo | null>(null);
  const [health, setHealth] = useState<HealthDetail | null>(null);
  const [showHealthy, setShowHealthy] = useState(false);
  const [editListenerUrl, setEditListenerUrl] = useState('');
  const [editInterval, setEditInterval] = useState(60);
  const [saving, setSaving] = useState(false);

  const isWriter = user?.role === 'admin' || user?.role === 'operator';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await apiClient.get(`/servers/${key}`);
        const srv = data.data;
        setServer(srv);

        // Health is fetched for every server, not just agent-heartbeat ones:
        // opening a server is usually an act of diagnosis, and this page used
        // to show configuration only — the state of its checks was a page away
        // with nothing linking to it.
        const healthRes = await apiClient.get(`/health/servers/${key}`).catch(() => null);
        if (healthRes?.data?.data) {
          setHealth(healthRes.data.data);
          if (healthRes.data.data.heartbeat) {
            setHeartbeat(healthRes.data.data.heartbeat);
          }
        }

        if (srv.checkMode === 'agent-heartbeat') {
          const cfgRes = await apiClient.get(`/servers/${key}/agent-config`);
          const cfg = cfgRes.data.data;
          setAgentConfig(cfg);
          setEditListenerUrl(cfg.listenerUrl || '');
          setEditInterval(cfg.interval || 60);
        }
      } catch (err: any) {
        message.error(err.response?.data?.error || 'Failed to load server');
        navigate('/servers');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [key, message, navigate]);

  const saveAgentConfig = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/servers/${key}/agent-config`, {
        listenerUrl: editListenerUrl,
        interval: editInterval,
      });
      message.success('Agent config updated');
      setAgentConfig((prev) => prev ? { ...prev, listenerUrl: editListenerUrl, interval: editInterval } : prev);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (!server) return null;

  const installCmd = `curl -sSL https://your-host/install-agent.sh | sudo bash -s -- \\
  --server-key ${server.key} \\
  --listener-url ${agentConfig?.listenerUrl || 'https://listener.example.com'} \\
  --interval ${agentConfig?.interval || 60}`;

  return (
    <>
      {/* Back, name and overall state on one line — three stacked blocks of
          chrome used to stand between the page title and its first fact. */}
      <Space style={{ marginBottom: 8 }} size={8} align="center">
        <Tooltip title="Back to Servers">
          <Button size="small" icon={<ArrowLeftOutlined />} aria-label="Back to Servers" onClick={() => navigate('/servers')} />
        </Tooltip>
        <Title level={4} style={{ margin: 0 }} ellipsis={{ tooltip: server.name }}>{server.name}</Title>
        {health?.status && (
          <Tag color={STATUS_COLORS[health.status] || 'default'} style={{ margin: 0 }}>
            {health.status}
          </Tag>
        )}
      </Space>

      {health && (() => {
        const rows = buildHealthRows(health);
        const problems = rows.filter((r) => !HEALTHY_STATES.includes(r.state));
        const visible = showHealthy ? rows : problems;
        return (
          <Card
            size="small"
            title={
              <Space>
                <HeartOutlined />
                <span>Health</span>
                {problems.length > 0
                  ? <Tag color="red">{problems.length} of {rows.length} failing</Tag>
                  : <Tag color="green">all {rows.length} checks healthy</Tag>}
              </Space>
            }
            extra={
              <Space>
                {rows.length > problems.length && (
                  <Button size="small" type="link" onClick={() => setShowHealthy((v) => !v)}>
                    {showHealthy ? 'Only problems' : 'Show healthy too'}
                  </Button>
                )}
                <Link to={`/servers/${server.key}/health`}>Full detail &amp; history →</Link>
              </Space>
            }
            style={{ marginBottom: 12 }}
          >
            {rows.length === 0 ? (
              <Text type="secondary">No check results yet — this server has not been probed.</Text>
            ) : (
              <Table<HealthRow>
                dataSource={visible}
                rowKey="key"
                size="small"
                pagination={false}
                columns={[
                  {
                    // The address is the primary line; what kind of endpoint it
                    // is and its modem label go underneath in secondary text.
                    title: 'Component', dataIndex: 'target', width: 220, ellipsis: true,
                    // A proxy row links to its own check history; ?proxy= puts
                    // that endpoint first on the health page with its steps open.
                    render: (target: string, r: HealthRow) => (
                      <Tooltip title={`${r.protocol ? `${r.protocol}://` : ''}${target}${r.label ? ` — ${r.label}` : ''}`} placement="topLeft">
                        <div style={{ lineHeight: 1.35, minWidth: 0 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.kind === 'proxy'
                              ? <Link to={`/servers/${server.key}/health?proxy=${encodeURIComponent(target)}`}>{target}</Link>
                              : target}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12, display: 'block' }} ellipsis>
                            {r.kind === 'proxy' ? (r.protocol || 'proxy') : 'network'}
                            {r.label && ` · ${r.label}`}
                          </Text>
                        </div>
                      </Tooltip>
                    ),
                  },
                  {
                    title: 'State', dataIndex: 'state', width: 96,
                    render: (s: string) => <Tag color={STATUS_COLORS[s] || 'default'} style={{ margin: 0, fontSize: 12 }}>{s}</Tag>,
                  },
                  {
                    title: 'Failed step', dataIndex: 'failedStep', width: 140, ellipsis: true,
                    render: (s?: string) => s ? <Text type="danger" style={{ fontSize: 12 }}>{s}</Text> : <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'Error', dataIndex: 'error', ellipsis: true,
                    render: (e?: string) => e
                      ? <Tooltip title={e} placement="topLeft"><Text type="danger" style={{ fontSize: 12 }}>{e}</Text></Tooltip>
                      : <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'Latency', dataIndex: 'latency', width: 76, align: 'right' as const,
                    render: (l: number) => l > 0 ? `${l.toFixed(0)}ms` : '—',
                  },
                  {
                    title: 'Checked', dataIndex: 'checkedAt', width: 84, align: 'right' as const,
                    render: (t: string) => t ? <Text type="secondary" style={{ fontSize: 12 }}>{timeAgo(t)}</Text> : <Text type="secondary">—</Text>,
                  },
                ]}
              />
            )}
          </Card>
        );
      })()}

      <Descriptions bordered column={2} size="small">
        {/* The name is the page title, so it is not repeated here. Pairs are
            ordered so every row is full — a lone cell before a span-2 row
            broke the grid. Mode and type are plain text: neither is a status. */}
        <Descriptions.Item label="Key">
          <Text copyable={{ tooltips: ['Copy key', 'Copied'] }}>{server.key}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Enabled">
          <Tag color={server.enabled ? 'green' : 'red'} style={{ margin: 0 }}>{server.enabled ? 'Yes' : 'No'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={server.type === 'mobile-gateway' ? 'Gateway IP' : 'Main IP'}>
          {server.mainIP}
          {server.type === 'mobile-gateway' && (
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
              Relay front — each modem tunnels out through its own carrier.
            </Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Country">{server.country || '—'}</Descriptions.Item>
        <Descriptions.Item label="Type">
          {server.type === 'mobile-gateway' ? 'Mobile gateway' : 'Dedicated'}
        </Descriptions.Item>
        <Descriptions.Item label="Check Mode">
          {server.checkMode === 'active-probing' ? 'Active Probing' : server.checkMode === 'agent-heartbeat' ? 'Agent Heartbeat' : server.checkMode}
        </Descriptions.Item>
        <Descriptions.Item label="Networks" span={2}>
          {(server.networks || []).length > 0
            ? <Flex wrap gap={4}>{server.networks.map((n) => <Tag key={n} style={{ margin: 0 }}>{n}</Tag>)}</Flex>
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Tags" span={2}>
          {(server.tags || []).length > 0
            ? <Flex wrap gap={4}>{server.tags.map((t) => <Tag key={t} style={{ margin: 0 }}>{t}</Tag>)}</Flex>
            : '—'}
        </Descriptions.Item>
        {server.sourceKey && (
          <Descriptions.Item label="Source" span={2}>
            <Text>{server.sourceKey}</Text>
            {server.sourceId && <Text type="secondary" style={{ marginLeft: 8 }}>ID {server.sourceId}</Text>}
            {server.sourceUrl && (
              <a href={server.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8 }}>
                {server.sourceUrl}
              </a>
            )}
          </Descriptions.Item>
        )}
        {server.meta && Object.keys(server.meta).length > 0 && (
          <Descriptions.Item label="Meta" span={2}>
            <pre
              style={{
                margin: 0,
                maxHeight: 140,
                overflow: 'auto',
                fontSize: 12,
                background: token.colorFillQuaternary,
                padding: 8,
                borderRadius: token.borderRadius,
              }}
            >
              {JSON.stringify(server.meta, null, 2)}
            </pre>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Created">{new Date(server.createdAt).toLocaleString()}</Descriptions.Item>
        <Descriptions.Item label="Updated">{new Date(server.updatedAt).toLocaleString()}</Descriptions.Item>
      </Descriptions>

      {server.checkMode === 'agent-heartbeat' && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Title level={5} style={{ marginTop: 0 }}><HeartOutlined /> Agent Heartbeat</Title>

          {heartbeat ? (
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Status">
                {heartbeat.lastHeartbeat ? (
                  <Tag color={STATUS_COLORS.HEALTHY}>Connected</Tag>
                ) : (
                  <Tag color={STATUS_COLORS.UNKNOWN}>No heartbeat</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Last Heartbeat">
                {heartbeat.lastHeartbeat ? timeAgo(heartbeat.lastHeartbeat) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Agent Version">
                {heartbeat.agentVersion || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Source IP">
                {heartbeat.sourceIP || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Tag color="default">No heartbeat received</Tag>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                Install the agent on this server to enable heartbeat monitoring.
              </Text>
            </Card>
          )}

          {isWriter && (
            // The two settings and their save button fit one line at this
            // width; they used to take four.
            <Card size="small" title="Agent Configuration" style={{ marginBottom: 16 }}>
              <Space wrap align="end" size={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Listener URL</Text>
                  <Input
                    size="small"
                    value={editListenerUrl}
                    onChange={(e) => setEditListenerUrl(e.target.value)}
                    placeholder="https://listener.example.com"
                    style={{ width: 320 }}
                  />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Heartbeat interval (s)</Text>
                  <InputNumber
                    size="small"
                    value={editInterval}
                    onChange={(v) => setEditInterval(v || 60)}
                    min={10}
                    max={3600}
                    style={{ width: 120 }}
                  />
                </div>
                <Button
                  size="small"
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={saveAgentConfig}
                  loading={saving}
                >
                  Save
                </Button>
              </Space>
            </Card>
          )}

          <Card size="small" title="Install Agent">
            <Paragraph style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Run this command on the target server to install and start the agent:</Text>
            </Paragraph>
            <pre style={{
              background: token.colorFillQuaternary,
              padding: 12,
              borderRadius: token.borderRadius,
              overflow: 'auto',
              fontSize: 12,
              margin: 0,
            }}>
              {installCmd}
            </pre>
          </Card>
        </>
      )}
    </>
  );
};

export default ServerDetailPage;
